import styles from './index.css';

// Please replace, if IE11-support isn't required any longer.
//import {useFormContext} from 'react-hook-form';
import {useFormContext} from 'react-hook-form/dist/react-hook-form.ie11';

import * as React from 'react';
import {get, pick} from 'lodash';
import classNames from 'classnames';

import * as COMPONENTS from '../../Components';
import {FormFieldProps} from '../../Definitions';
import Note from '../Note';

type RenderInputProps = {
	type: string;
	[key: string]: any;
}
const renderInput = ({type, ...props}: RenderInputProps):React.ReactNode|null => {
	switch (type) {

		case 'CheckboxGroup':
			return <COMPONENTS.CheckboxGroup {...pick(props, ['name', 'required', 'options', 'visibleElements'])}/>;

		case 'ControlledMultiSelectbox':
			return <COMPONENTS.ControlledMultiSelectbox {...pick(props, ['onSelect', 'selectedValues', 'isInvalid', 'options', 'hasLoader', 'required'])}/>;

		case 'DateInput':
			return <COMPONENTS.DateInput {...pick(props, ['name', 'required', 'onFocus', 'onBlur', 'minDate', 'maxDate', 'format'])}/>;

		case 'MultiSelectbox':
			return <COMPONENTS.MultiSelectbox {...pick(props, ['name', 'required', 'options', 'hasLoader'])}/>;

		case 'NumberInput':
			return <COMPONENTS.NumberInput {...pick(props, ['name', 'required', 'options', 'onFocus', 'onBlur', 'min', 'max', 'steps'])}/>;

		case 'ReCaptcha':
			return <COMPONENTS.ReCaptcha {...pick(props, ['name', 'sitekey'])}/>;

		case 'RadioGroup':
			return <COMPONENTS.RadioGroup {...pick(props, ['name', 'required', 'options'])}/>;

		case 'Selectbox':
			return <COMPONENTS.Selectbox {...pick(props, ['name', 'required', 'hasEmptyOption', 'options', 'onChange'])}/>;

		case 'TextArea':
			return <COMPONENTS.TextArea {...pick(props, ['name', 'required', 'onFocus', 'onBlur', 'maxLength'])}/>;

		case 'TextInput':
			return <COMPONENTS.TextInput {...pick(props, ['name', 'required', 'pattern', 'maxLength', 'onFocus', 'onBlur', 'validate'])}/>;

        case 'TextInputDisabled':
            return <COMPONENTS.TextInput {...pick(props, ['name', 'required', 'pattern', 'onBlur', 'validate', 'readOnly', 'deletable'])}/>;

		default:
			break;
	}

	return null;
}

const getLayout = (type: string):string => {
	switch (type) {
		case 'CheckboxGroup':
			return styles.isColumn;

		case 'RadioGroup':
			return styles.isRow;

		case 'ControlledMultiSelectbox':
		case 'MultiSelectbox':
		case 'Selectbox':
			return classNames(styles.isAnimated, styles.noPointerEvents, styles.hasIcon);

		case 'DateInput':
			return classNames(styles.isAnimated, styles.hasIcon);

		case 'NumberInput':
			return classNames(styles.isAnimated, styles.hasTwoIcon);

		case 'ReCaptcha':
			return classNames(styles.isAnimated, styles.fixedTop);

		case 'TextArea':
		case 'TextInput':
        case 'TextInputDisabled':
		default:
			return styles.isAnimated;
	}
}

export default (props: FormFieldProps) => {
	let isRendered = true;

	const [isFocussed, setIsFocussed] = React.useState(false);
	const {errors, getValues, watch} = useFormContext();
	const {type = '', name, label, additionalLabel, required, className, watch: watchArgs, note = '', isInvalid, hasValue} = props;

	const onFocus = React.useCallback(() => setIsFocussed(true), [setIsFocussed]);
	const onBlur = React.useCallback(() => setIsFocussed(false), [setIsFocussed]);
	const hasErrors = isInvalid === undefined
		? Boolean(get(errors, name))
		: isInvalid;
	const value = get(getValues({nest: true}), name);
	const valueIsSet = hasValue !== undefined
		? hasValue
		: Array.isArray(value)
			? value.length > 0
			: Boolean(value);

	if (watchArgs) {
		const watchValue = watch(watchArgs.name);

		isRendered = watchArgs.value === undefined
			? Boolean(watchValue)
			: Array.isArray(watchValue)
				? watchValue.includes(watchArgs.value)
				: watchValue === watchArgs.value;
	}

	return isRendered
		? (
			<div
				data-invalid={hasErrors ? '1' : '0'}
				className={classNames(
					styles.formfield,
					getLayout(type),
					isFocussed && styles.isFocussed,
					hasErrors && styles.isInvalid,
					valueIsSet && styles.hasValue,
					!valueIsSet && required && styles.isRequired,
					className,
				)}
			>
				{
					Boolean(label) &&
						<label
							htmlFor={name}
							className={styles.label}
						>
							{
								[
									label,
									additionalLabel,
									required && '*'
								]
									.filter(i => i)
									.join(' ')
							}
						</label>
				}
				{
					renderInput({
						...props,
						onFocus,
						onBlur
					})
				}
				{
					Boolean(note) &&
						<Note text={note}/>
				}
			</div>
		)
		: null
}
